import * as Sentry from "@sentry/react";

import { call, put, retry, takeLatest } from "redux-saga/effects";

import { AxiosResponse } from "axios";
import { BASE_API_URL } from "../../config";
import { actions } from "./slice";
import { createRequest } from "../../utils/requestUtils";

export function* fetchCases() {
  const url = `${BASE_API_URL}/cases`;

  try {
    const { data }: AxiosResponse = yield retry(5, 1500, createRequest, {
      url: url,
    });

    yield put(actions.requestCasesSuccessful(data.payload));
  } catch (error) {
    Sentry.setTag("apiUrl", url);
    Sentry.setContext("saga", {
      name: "Cases",
      function: "fetchCases",
      message: "Could not get cases from API",
    });
    Sentry.captureException(error);
    const errorMessage = error instanceof Error ? error.toString() : "error";
    yield put(actions.requestCasesFailed(errorMessage));
  }
}

export function* submitEvidence({
  payload,
}: ReturnType<typeof actions.requestSubmitEvidence>) {
  const { caseId, evidenceId, message, attachments } = payload;
  const url = `${BASE_API_URL}/cases/${caseId}/evidences/${evidenceId}`;

  try {
    if (attachments) {
      const attachmentIdList = attachments.map(({ fileId }) => fileId);

      const body = {
        attachments: attachmentIdList,
        message: message,
      };

      const { data }: AxiosResponse = yield call(createRequest, {
        method: "patch",
        url: url,
        requestBody: body,
      });

      yield put(actions.requestSubmitEvidenceSuccessful(data.payload));
    }
  } catch (error) {
    Sentry.setTag("apiUrl", url);
    Sentry.setContext("saga", {
      name: "Cases",
      function: "submitEvidence",
      withAttachment: !!attachments,
      url: url,
    });
    Sentry.captureEvent({
      message: "Could not submit evidence",
    });

    yield put(
      actions.requestSubmitEvidenceFailed({
        evidenceId: payload.evidenceId,
        message: "Kunne ikke sende svaret. Prøv igjen.",
      }),
    );
  }
}

function* toggleEvidenceRead({
  payload,
}: ReturnType<typeof actions.requestToggleEvidenceRead>) {
  const { caseId, evidenceId, read } = payload;
  const url = `${BASE_API_URL}/cases/${caseId}/evidences/${evidenceId}/customer/status`;

  try {
    const { data }: AxiosResponse = yield retry(5, 1500, createRequest, {
      method: "patch",
      url: url,
      requestBody: { read: read },
    });

    yield put(actions.requestToggleEvidenceReadSuccessful(data.payload));
  } catch (error) {
    Sentry.setTag("apiUrl", url);
    Sentry.setContext("saga", {
      name: "Cases",
      function: "toggleEvidenceRead",
    });
    Sentry.captureEvent({
      message: "Unable to mark an evidence as read",
    });
    const errorMessage = error instanceof Error ? error.toString() : "error";
    yield put(actions.requestToggleEvidenceReadFailed(errorMessage));
  }
}

function* casesSaga() {
  yield takeLatest(actions.requestCases, fetchCases);
  yield takeLatest(actions.requestSubmitEvidence, submitEvidence);
  yield takeLatest(actions.requestToggleEvidenceRead, toggleEvidenceRead);
}

export default casesSaga;
